/**
 * Add/Edit Location Modal Component
 * 
 * Modal for adding or editing favorite locations
 * Uses the common Modal component for consistency
 */

import React, { useState, useEffect, useRef, useContext } from 'react';
import { FaSave } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { supabase } from '../data/supabaseClient';
import { AuthContext } from '../Provider';
import { useToast } from '../hooks/ToastContext';
import { getCoordinates } from '../../utils/geocodingUtils';
import Spinner from '../common/Spinner';
import Modal from '../common/Modal';
import { Checkbox1 } from '../Templates';
import './AddLocationModal.css';

const AddLocationModal = ({ isOpen, location, onSave, onClose }) => {
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  const addressInputRef = useRef(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    label: location?.label || '',
    address: location?.address || '',
    city: location?.city || '',
    latitude: location?.latitude || null,
    longitude: location?.longitude || null,
    place_id: location?.place_id || null,
    radius_km: location?.radius_km || 5,
    notify_for_rides: location?.notify_for_rides !== false,
    notify_for_packages: location?.notify_for_packages !== false,
    match_start_point: location?.match_start_point !== false,
    match_end_point: location?.match_end_point !== false
  });

  // Reset form when location changes
  useEffect(() => {
    if (location) {
      setFormData({
        label: location.label || '',
        address: location.address || '',
        city: location.city || '',
        latitude: location.latitude || null,
        longitude: location.longitude || null,
        place_id: location.place_id || null,
        radius_km: location.radius_km || 5,
        notify_for_rides: location.notify_for_rides !== false,
        notify_for_packages: location.notify_for_packages !== false,
        match_start_point: location.match_start_point !== false,
        match_end_point: location.match_end_point !== false
      });
    } else {
      // Reset to defaults for new location
      setFormData({
        label: '',
        address: '',
        city: '',
        latitude: null,
        longitude: null,
        place_id: null,
        radius_km: 5,
        notify_for_rides: true,
        notify_for_packages: true,
        match_start_point: true,
        match_end_point: true
      });
    }
  }, [location, isOpen]);

  useEffect(() => {
    // Check if Google Maps is loaded
    const checkGoogleMaps = () => {
      if (window.google?.maps) {
        setGoogleMapsLoaded(true);
        initializeAutocomplete();
      } else {
        setTimeout(checkGoogleMaps, 500);
      }
    };
    
    if (isOpen) {
      checkGoogleMaps();
    }
  }, [isOpen]);

  const initializeAutocomplete = () => {
    if (!addressInputRef.current || !window.google?.maps) return;

    const autocomplete = new window.google.maps.places.Autocomplete(
      addressInputRef.current,
      { types: ['geocode'] }
    );

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const cityComponent = place.address_components?.find(
          c => c.types.includes('locality') || c.types.includes('administrative_area_level_2')
        );

        setFormData(prev => ({
          ...prev,
          address: place.formatted_address,
          city: cityComponent?.long_name || '',
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
          place_id: place.place_id
        }));
      }
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.label.trim()) {
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: t('settings.location_name_required') || 'Location name is required'
      });
      return false;
    }

    if (!formData.address.trim()) {
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: t('settings.address_required') || 'Address is required'
      });
      return false;
    }

    if (!formData.latitude || !formData.longitude) {
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: t('settings.select_valid_address') || 'Please select a valid address from the dropdown'
      });
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      // If coordinates are missing, geocode the address
      if (!formData.latitude || !formData.longitude) {
        const result = await getCoordinates(formData.address);
        if (result.success) {
          formData.latitude = result.data.latitude;
          formData.longitude = result.data.longitude;
          formData.place_id = result.data.place_id;
          formData.city = result.data.city;
        } else {
          throw new Error('Could not geocode address');
        }
      }

      const dataToSave = {
        user_id: user.id,
        label: formData.label.trim(),
        address: formData.address.trim(),
        city: formData.city || null,
        latitude: formData.latitude,
        longitude: formData.longitude,
        place_id: formData.place_id,
        radius_km: formData.radius_km,
        notify_for_rides: formData.notify_for_rides,
        notify_for_packages: formData.notify_for_packages,
        match_start_point: formData.match_start_point,
        match_end_point: formData.match_end_point,
        is_active: true
      };

      let savedLocation;

      if (location?.id) {
        // Update existing location
        const { data, error } = await supabase
          .from('user_favorite_locations')
          .update(dataToSave)
          .eq('id', location.id)
          .eq('user_id', user.id)
          .select()
          .single();

        if (error) throw error;
        savedLocation = data;

        addToast({
          type: 'success',
          title: t('settings.success'),
          description: t('settings.location_updated')
        });
      } else {
        // Create new location
        const { data, error } = await supabase
          .from('user_favorite_locations')
          .insert(dataToSave)
          .select()
          .single();

        if (error) throw error;
        savedLocation = data;

        addToast({
          type: 'success',
          title: t('settings.success'),
          description: t('settings.location_added') || 'Location added successfully'
        });
      }

      onSave(savedLocation);

    } catch (error) {
      console.error('Error saving location:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: error.message || t('settings.location_save_failed') || 'Failed to save location'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={location ? t('settings.edit_location') : t('settings.add_favorite_location')}
      modalSize="medium"
    >
      <div className="location-modal-body">
        {/* Location Name */}
        <div className="location-form-group">
          <label>{t('settings.location_name')} *</label>
          <input
            type="text"
            value={formData.label}
            onChange={(e) => handleChange('label', e.target.value)}
            placeholder={t('settings.location_name_placeholder')}
            maxLength={100}
          />
        </div>

        {/* Address with Google Autocomplete */}
        <div className="location-form-group">
          <label>{t('settings.address')} *</label>
          <input
            ref={addressInputRef}
            type="text"
            value={formData.address}
            onChange={(e) => handleChange('address', e.target.value)}
            placeholder={t('settings.address_placeholder') || 'Start typing an address...'}
            disabled={!googleMapsLoaded}
          />
          {!googleMapsLoaded && <p className="location-help-text">{t('settings.loading_maps') || 'Loading Google Maps...'}</p>}
        </div>

        {/* Radius Slider */}
        <div className="location-form-group">
          <label>{t('settings.search_radius')}: {formData.radius_km}km</label>
          <input
            type="range"
            min="5"
            max="20"
            step="5"
            value={formData.radius_km}
            onChange={(e) => handleChange('radius_km', parseInt(e.target.value))}
            className="location-radius-slider"
          />
          <div className="location-slider-labels">
            <span>5km</span>
            <span>10km</span>
            <span>15km</span>
            <span>20km</span>
          </div>
        </div>

        {/* Match Points */}
        <div className="location-form-group">
          <label>{t('settings.notify_when_posts')}</label>
          <div className="location-checkbox-group">
            <Checkbox1
              id="match-start"
              label={`🚀 ${t('settings.start_near_location') || 'START near this location'}`}
              checked={formData.match_start_point}
              onChange={(e) => handleChange('match_start_point', e.target.checked)}
            />
            <Checkbox1
              id="match-end"
              label={`🏁 ${t('settings.end_near_location') || 'END near this location'}`}
              checked={formData.match_end_point}
              onChange={(e) => handleChange('match_end_point', e.target.checked)}
            />
          </div>
        </div>

        {/* Notify For */}
        <div className="location-form-group">
          <label>{t('settings.notify_me_about')}</label>
          <div className="location-checkbox-group">
            <Checkbox1
              id="notify-rides"
              label={`🚗 ${t('settings.rides_drivers_passengers') || 'Rides (drivers & passengers)'}`}
              checked={formData.notify_for_rides}
              onChange={(e) => handleChange('notify_for_rides', e.target.checked)}
            />
            <Checkbox1
              id="notify-packages"
              label={`📦 ${t('settings.package_deliveries') || 'Package deliveries'}`}
              checked={formData.notify_for_packages}
              onChange={(e) => handleChange('notify_for_packages', e.target.checked)}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="location-modal-footer">
          <button className="location-btn-cancel" onClick={onClose} disabled={saving}>
            {t('buttons.cancel')}
          </button>
          <button className="location-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner size="small" showText={false} />
                {t('settings.saving')}...
              </>
            ) : (
              <>
                <FaSave /> {t('settings.save_location')}
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddLocationModal;
